import {
    Editor,
    Element,
    Point,
    Range,
    Transforms,
    type BlockNode,
    type Node,
} from "slate";

import {unwrapList} from "../helpers/list-helpers.ts";

/**
 * This is a Slate plugin that adds support for resetting the block type when
 * delete/backspace is typed at the beginning of a block. For example, if in
 * an empty heading node, a single backspace will turn the block into a plain
 * paragraph. Another backspace at that point will remove the paragraph and
 * move the cursor up to the end of the previous block, as usual.
 */
export const withSmartDelete = (editor: Editor): Editor => {
    const {deleteBackward} = editor;

    editor.deleteBackward = (...args) => {
        const {selection} = editor;

        if (selection && Range.isCollapsed(selection)) {
            const parentBlock = Editor.above<BlockNode>(editor, {
                match: (n) =>
                    !Editor.isEditor(n) &&
                    Element.isElement(n) &&
                    Editor.isBlock(editor, n),
            });

            if (parentBlock) {
                const [parentNode, parentPath] = parentBlock;
                const start = Editor.start(editor, parentPath);

                if (
                    !Editor.isEditor(parentNode) &&
                    Element.isElement(parentNode) &&
                    parentNode.type !== "paragraph" &&
                    Point.equals(selection.anchor, start)
                ) {
                    if (isPartOfList(parentNode)) {
                        // If we're inside a list, we need to take care to unwrap all
                        // the nested blocks that comprise a list.
                        unwrapList(editor);
                    } else {
                        // Otherwise, we just switch the block type to paragraph.
                        Transforms.setNodes(editor, {type: "paragraph"});
                    }

                    return;
                }
            }
        }

        deleteBackward(...args);
    };

    return editor;
};

const isPartOfList = (n: Node) =>
    Element.isElement(n) &&
    (n.type === "list-item" ||
        n.type === "list-item-text" ||
        n.type === "bulleted-list" ||
        n.type === "numbered-list" ||
        n.type === "lettered-list");
